import React, { useState, useEffect } from "react";
import { func, number, oneOfType, shape, string, arrayOf, bool } from "prop-types";
import * as config from "util/config";
import * as i18nUtils from "util/i18n";
import classNames from "classnames";
import I18n from "pages/_components/I18n";
import Button from "pages/_components/Button";
import CustomText from "pages/_components/CustomText/CustomText";
import { useDispatch, connect, useSelector } from "react-redux";
import { actions as notificationActions } from "reducers/notification";
import { actions as comexActions } from "reducers/comex";
import FieldError from "pages/_components/fields/FieldError";
import Spinner from "pages/_components/WidgetLoading";
import { isMobile } from "react-device-detect";

const DocumentUploader = ({
    className,
    label,
    documentCode,
    form,
    field,
    onChange,
    defaultDocument,
    isValid,
    overrideDocumentName,
    allowedTypes, // ["pdf", "png"]
    multiUploaderId,
    withDocumentName,
}) => {
    const dispatch = useDispatch();
    const { documentToUploadRequest = {}, comexId = null } = useSelector((state) => state.comex);

    const {
        id: documentToUploadRequestId,
        status: documentToUploadRequestStatus,
        value: documentToUploadRequestValue,
        error: documentToUploadRequestError,
    } = documentToUploadRequest;

    const documentInputRef = React.useRef(null);
    const uploaderRequestId = multiUploaderId || documentCode;
    const [document, setDocument] = useState(null);
    const [documentUploading, setDocumentUploading] = useState(null);
    const [documentIsUploading, setDocumentIsUploading] = useState(false);
    const [hasInvalidType, setHasInvalidType] = useState(false);
    const [alreadyResetDocument, setAlreadyResetDocument] = useState(false);
    const [lastDocumentToUploadRequest, setLastDocumentToUploadRequest] = useState({
        status: "INITIAL",
        value: "",
        error: null,
    });
    const {
        status: lastDocumentToUploadRequestStatus,
        value: lastDocumentToUploadRequestValue,
        error: lastDocumentToUploadRequestError,
    } = lastDocumentToUploadRequest;

    const MAX_FILE_SIZE = config.getInteger("comex.maxFileSize", 5242880); // 5MB por defecto

    const handleDelete = () => {
        setDocument({});
        setDocumentIsUploading(false);
        setLastDocumentToUploadRequest({
            status: "INITIAL",
            value: "",
            error: null,
        });
        form.setFieldValue(field.name, "", false);
        if (withDocumentName) {
            form.setFieldValue(`${field.name}Name`, "", false);
        }
        onChange("");

        dispatch(comexActions.documentDelete(document.documentId));
    };

    /**
     * Estado para no mostrar la notificación en caso de haberse cargado el documento previamente
     * y regresar a la pantalla
     */
    const [documentNotificationDisabled, setDocumentNotificationDisabled] = useState(true);

    useEffect(() => {
        if (defaultDocument?.documentId) {
            setDocumentUploading(defaultDocument);
            setDocumentIsUploading(true);
            setLastDocumentToUploadRequest({
                status: "SUCCESS",
                value: { documentId: defaultDocument.documentId },
                error: null,
            });
        }
    }, [defaultDocument?.documentId]);

    useEffect(() => {
        if (documentToUploadRequestId === uploaderRequestId) {
            setLastDocumentToUploadRequest({
                status: documentToUploadRequestStatus,
                value: documentToUploadRequestValue,
                error: documentToUploadRequestError,
            });
        }
    }, [
        uploaderRequestId,
        documentToUploadRequestId,
        documentToUploadRequestStatus,
        documentToUploadRequestValue,
        documentToUploadRequestError,
    ]);

    useEffect(() => {
        if (lastDocumentToUploadRequestStatus === "SUCCESS") {
            setHasInvalidType(false);
            form.setFieldValue(field.name, lastDocumentToUploadRequestValue?.documentId, false);
            if (withDocumentName) {
                form.setFieldValue(`${field.name}Name`, documentUploading?.documentName, false);
            }
            onChange({ ...documentUploading, documentId: lastDocumentToUploadRequestValue?.documentId });
            setDocument({ ...documentUploading, documentId: lastDocumentToUploadRequestValue?.documentId });

            if (!documentNotificationDisabled) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get("", "Archivo cargado correctamente."),
                        "success",
                        ["ComexNotification", "StepComexClearance"],
                    ),
                );
            }
            dispatch(comexActions.resetDocumentToUploadRequest());
            setDocumentIsUploading(false);
        } else if (lastDocumentToUploadRequestStatus === "FAILURE") {
            setHasInvalidType(false);
            setDocument(documentUploading);
            dispatch(
                notificationActions.showNotification(
                    i18nUtils.get(
                        "",
                        lastDocumentToUploadRequestError ||
                            "Formato de archivo invalido. Por favor, ingresarlo en función de las instrucciones provistas.",
                    ),
                    "error",
                    ["ComexNotification", "StepComexClearance"],
                ),
            );
            setDocumentIsUploading(false);
        }
    }, [lastDocumentToUploadRequestStatus, lastDocumentToUploadRequestValue, lastDocumentToUploadRequestError]);

    useEffect(() => {
        if (!isValid && !alreadyResetDocument) {
            setLastDocumentToUploadRequest({
                status: "INITIAL",
                value: "",
                error: null,
            });
            setHasInvalidType(false);
            setDocument({});
            form.setFieldValue(field.name, "", false);
            if (withDocumentName) {
                form.setFieldValue(`${field.name}Name`, "", false);
            }
            onChange("");
            setAlreadyResetDocument(true);
        } else if (isValid && alreadyResetDocument) {
            setAlreadyResetDocument(false);
        }
    }, [isValid, alreadyResetDocument, form, field]);

    const uploadFile = () => {
        if (!documentIsUploading) {
            documentInputRef.current.click();
            form.setFieldTouched(field.name);
        }
    };

    const getBase64 = (nativeDocument) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(nativeDocument);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const onChangeDocument = async (event) => {
        event.stopPropagation();
        event.preventDefault();
        setDocumentNotificationDisabled(!documentNotificationDisabled);
        const newDocument = event.target.files[0];
        if (newDocument) {
            const allowedFileExtensions = allowedTypes || config.get("files.allowedFileExtensions");
            const documentExtensionSliced = `${newDocument.name.split(".").slice(-1)}`;
            const documentExtension = documentExtensionSliced?.toLowerCase();
            const sanitizedName = overrideDocumentName
                ? `${overrideDocumentName}.${documentExtension}`
                : newDocument.name;

            // elimino el sufijo agregado en el componente para evitar comportamiento indeseado cuando el id es el mismo
            documentCode = removeSuffixCode(documentCode);

            if (allowedFileExtensions.includes(documentExtension)) {
                if (newDocument.size > MAX_FILE_SIZE) {
                    dispatch(
                        notificationActions.showNotification(
                            i18nUtils.get("", "El archivo excede el tamaño máximo permitido."),
                            "error",
                            ["ComexNotification", "StepComexClearance"],
                        ),
                    );
                    setHasInvalidType(true);
                    setDocument({
                        documentCode,
                        documentName: newDocument.name,
                        size: newDocument.size,
                        type: documentExtension,
                    });
                    return;
                }

                const extractData = (base64String) =>
                    base64String
                        .replace("data:image/", "")
                        .split(";")[1]
                        .replace("base64,", "");

                getBase64(newDocument).then((base64) => {
                    const documentToUpload = {
                        uploaderRequestId,
                        documentCode,
                        documentName: newDocument.name,
                        _documentToSave: extractData(base64),
                        step: "stepADITIONALDOCUMENT",
                        comexId,
                    };
                    setDocumentIsUploading(true);
                    setDocumentUploading({
                        ...documentToUpload,
                        size: newDocument.size,
                        type: documentExtension,
                    });

                    dispatch(comexActions.documentUpload(documentToUpload));
                });
            } else {
                setDocument({
                    documentCode,
                    documentName: newDocument.name,
                    size: newDocument.size,
                    type: documentExtension,
                });
                setHasInvalidType(true);
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            "",
                            "Formato de archivo invalido. Por favor, ingresarlo en función de las instrucciones provistas.",
                        ),
                        "error",
                        ["ComexNotification", "StepComexClearance"],
                    ),
                );
            }
        }

        documentInputRef.current.value = "";
    };

    const removeSuffixCode = (str) => {
        const lastUnderscore = str.lastIndexOf("_");
        if (lastUnderscore === -1) return str;
        return str.substring(0, lastUnderscore);
    };

    const hasError = form.touched[field.name] && form.errors[field.name];

    const uploaderByStatus = () => {
        if (lastDocumentToUploadRequestStatus === "SUCCESS") {
            return (
                <div className="singleline-uploader processing-complete uploader-container">
                    <div>
                        <div>
                            <I18n defaultValue="Carga completa" component="strong" />
                        </div>
                        <I18n defaultValue={`${document?.documentName}`} />
                    </div>
                    <Button
                        onClick={handleDelete}
                        className="btn btn-outline uploader-delete-btn"
                        label="global.delete"
                        image="images/trash.svg"
                        block={false}
                        bsStyle="text"
                    />
                </div>
            );
        }

        if (lastDocumentToUploadRequestStatus === "FAILURE" || hasInvalidType) {
            return (
                <div className="singleline-uploader has-error">
                    <div>
                        <p>
                            <I18n
                                defaultValue={
                                    hasInvalidType ? `Formato de archivo inválido.` : "Error durante la carga"
                                }
                            />
                        </p>
                        <I18n defaultValue={`${document?.documentName}  ${document?.size} bytes.`} />
                    </div>
                    <Button
                        block={false}
                        onClick={uploadFile}
                        className="btn btn-small"
                        bsStyle="text"
                        defaultLabelText={i18nUtils.get("", "Volver a subir")}
                        image="images/figma/icon/upload.svg"
                    />
                </div>
            );
        }

        return (
            <div
                className={classNames("form-group singleline-uploader btn-uploader", {
                    "has-error": hasError,
                })}>
                <div
                    className={className !== null ? className : "box transparent box-border-dashed flex"}
                    onClick={uploadFile}>
                    {!documentIsUploading ? (
                        <>
                            <Button
                                className="btn-secondary icon-medium btn-only-icon enphasize uploader-icon"
                                image="images/figma/icon/add.svg"
                                block={false}
                                bsStyle="text"
                                disabled={documentIsUploading}
                            />
                            <Button
                                bsStyle="text"
                                block={false}
                                className="text-left px-6 py-0"
                                disabled={documentIsUploading}>
                                <CustomText text={label} textType="I18N" />
                            </Button>
                        </>
                    ) : (
                        <Spinner />
                    )}
                </div>

                {hasError && <FieldError error={form.errors[field.name]} />}
            </div>
        );
    };

    return (
        <div>
            {uploaderByStatus()}
            <input
                id="myInput"
                type="file"
                ref={documentInputRef}
                style={{ display: "none" }}
                onChange={onChangeDocument}
            />
        </div>
    );
};

DocumentUploader.propTypes = {
    label: string.isRequired,
    documentCode: string.isRequired,
    overrideDocumentName: string,
    field: shape({
        onBlur: func,
        onChange: func,
        name: string,
        value: oneOfType([number, string]),
    }).isRequired,
    form: shape({
        errors: shape({}),
        touched: shape({}),
    }).isRequired,
    className: string,
    onChange: func,
    defaultDocument: shape({}),
    isValid: bool,
    allowedTypes: arrayOf(string),
    multiUploaderId: string,
    withDocumentName: bool,
};

DocumentUploader.defaultProps = {
    className: null,
    overrideDocumentName: null,
    onChange: () => {},
    defaultDocument: {},
    isValid: true,
    allowedTypes: null,
    multiUploaderId: null,
    withDocumentName: false,
};

export default DocumentUploader;
